import { useState } from "react";
import { useUserAccounts } from "@trader-dashboard/shared";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { httpsCallable, getFunctions } from 'firebase/functions';
import { arrayToCSV, downloadCSV } from '@/utils/csvExport';
import { formatCurrency } from '@/utils/formatCurrency';

interface OrderDetail {
  ticket: number;
  symbol: string;
  type: number;
  lots: number;
  openPrice: number;
  openTime: string;
  closePrice?: number;
  closeTime?: string;
  profit: number;
  swap: number;
  commission: number;
  netProfit: number;
  status: string;
}

interface MonthlyDrillDownData {
  year: number;
  month: number;
  monthName: string;
  closedOrders: OrderDetail[];
  openOrders: OrderDetail[];
  closedOrdersTotal: number;
  openOrdersTotal: number;
  closedOrdersCount: number;
  openOrdersCount: number;
}

const MONTHS = [
  { value: '0', label: 'January' },
  { value: '1', label: 'February' },
  { value: '2', label: 'March' },
  { value: '3', label: 'April' },
  { value: '4', label: 'May' },
  { value: '5', label: 'June' },
  { value: '6', label: 'July' },
  { value: '7', label: 'August' },
  { value: '8', label: 'September' },
  { value: '9', label: 'October' },
  { value: '10', label: 'November' },
  { value: '11', label: 'December' },
];

const ORDER_TYPES: { [key: number]: string } = {
  0: 'Buy',
  1: 'Sell',
  2: 'Buy Limit',
  3: 'Sell Limit',
  4: 'Buy Stop',
  5: 'Sell Stop',
};

export const MonthlyDrillDownPage = (): React.ReactNode => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth.toString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [reportData, setReportData] = useState<MonthlyDrillDownData | undefined>(undefined);
  const [searchTicket, setSearchTicket] = useState<string>('');

  const { selectedAccountId } = useUserAccounts();

  // Generate year options (current year and 5 years back)
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const loadReport = async () => {
    if (!selectedAccountId || !selectedYear || selectedMonth === undefined) return;

    setLoading(true);
    setError(undefined);

    try {
      const functions = getFunctions();
      const getMonthlyDrillDownReport = httpsCallable<
        { accountId: string; year: number; month: number },
        MonthlyDrillDownData
      >(functions, 'getMonthlyDrillDownReport');

      const result = await getMonthlyDrillDownReport({
        accountId: selectedAccountId,
        year: parseInt(selectedYear),
        month: parseInt(selectedMonth),
      });
      setReportData(result.data);
    } catch (err: any) {
      console.error('Error loading monthly drill-down report:', err);
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString: string) => {
    return new Date(isoString).toLocaleString('en-US', { timeZone: 'UTC', timeZoneName: 'short' });
  };

  const formatNumber = (num: number, decimals: number = 2) => {
    return num.toFixed(decimals);
  };

  const exportToCSV = (orders: OrderDetail[], type: 'closed' | 'open') => {
    if (!reportData) return;

    const headers = [
      { key: 'ticket' as const, label: 'Ticket' },
      { key: 'symbol' as const, label: 'Symbol' },
      { key: 'type' as const, label: 'Type' },
      { key: 'lots' as const, label: 'Lots' },
      { key: 'openPrice' as const, label: 'Open Price' },
      { key: 'openTime' as const, label: 'Open Time' },
      { key: 'closePrice' as const, label: 'Close Price' },
      { key: 'closeTime' as const, label: 'Close Time' },
      { key: 'profit' as const, label: 'Gross Profit' },
      { key: 'swap' as const, label: 'Swap' },
      { key: 'commission' as const, label: 'Commission' },
      { key: 'netProfit' as const, label: 'Net P/L' },
      { key: 'status' as const, label: 'Status' },
    ];

    // Prepare data with formatted values
    const formattedOrders = orders.map(order => ({
      ...order,
      type: ORDER_TYPES[order.type] || order.type,
      lots: formatNumber(order.lots),
      openPrice: formatNumber(order.openPrice, 5),
      openTime: formatDateTime(order.openTime),
      closePrice: order.closePrice ? formatNumber(order.closePrice, 5) : '',
      closeTime: order.closeTime ? formatDateTime(order.closeTime) : '',
      profit: formatNumber(order.profit),
      swap: formatNumber(order.swap),
      commission: formatNumber(order.commission),
      netProfit: formatNumber(order.netProfit),
    }));

    const csv = arrayToCSV(formattedOrders, headers);
    const filename = `${reportData.monthName}_${reportData.year}_${type}_orders.csv`;
    downloadCSV(csv, filename);
  };

  const renderOrdersTable = (orders: OrderDetail[], title: string, totalProfit: number, type: 'closed' | 'open') => {
    // Filter orders by search ticket
    const filteredOrders = searchTicket.trim()
      ? orders.filter(order => order.ticket.toString().includes(searchTicket.trim()))
      : orders;

    const filteredTotal = filteredOrders.reduce((sum, order) => sum + order.netProfit, 0);

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportToCSV(filteredOrders, type)}
              className="mt-2"
              disabled={filteredOrders.length === 0}
            >
              Download CSV ({filteredOrders.length} orders)
            </Button>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">
              {searchTicket.trim() ? `Filtered: ${filteredOrders.length} / ${orders.length}` : `Total Orders: ${orders.length}`}
            </div>
            <div className={`text-lg font-bold ${filteredTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              Total P/L: {formatCurrency(filteredTotal)}
            </div>
          </div>
        </div>
        <div className="border rounded-lg overflow-auto max-h-[600px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>Ticket</TableHead>
                <TableHead>Symbol</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Lots</TableHead>
                <TableHead className="text-right">Open Price</TableHead>
                <TableHead>Open Time</TableHead>
                <TableHead className="text-right">Close Price</TableHead>
                <TableHead>Close Time</TableHead>
                <TableHead className="text-right">Gross Profit</TableHead>
                <TableHead className="text-right">Swap</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead className="text-right">Net P/L</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order) => (
                <TableRow key={order.ticket}>
                  <TableCell className="font-mono">{order.ticket}</TableCell>
                  <TableCell className="font-semibold">{order.symbol.toUpperCase()}</TableCell>
                  <TableCell>{ORDER_TYPES[order.type] || order.type}</TableCell>
                  <TableCell className="text-right">{formatNumber(order.lots)}</TableCell>
                  <TableCell className="text-right">{formatNumber(order.openPrice, 5)}</TableCell>
                  <TableCell className="text-sm">{formatDateTime(order.openTime)}</TableCell>
                  <TableCell className="text-right">
                    {order.closePrice ? formatNumber(order.closePrice, 5) : '-'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {order.closeTime ? formatDateTime(order.closeTime) : '-'}
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(order.profit)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(order.swap)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(order.commission)}</TableCell>
                  <TableCell className={`text-right font-semibold ${order.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(order.netProfit)}
                  </TableCell>
                </TableRow>
              ))}
              {filteredOrders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className="text-center text-muted-foreground py-8">
                    No orders found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Monthly Drill-Down Report</CardTitle>
          <CardDescription>
            View detailed order breakdown for a specific month
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Select Year</label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Select Month</label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(month => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={loadReport} disabled={loading}>
              {loading ? 'Loading...' : 'Generate Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card>
          <CardContent className="p-8">
            <div className="text-center text-red-600">Error: {error}</div>
          </CardContent>
        </Card>
      )}

      {reportData && (
        <Card>
          <CardHeader>
            <CardTitle>{reportData.monthName} {reportData.year} - Detailed Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Input
                type="text"
                placeholder="Search by ticket number..."
                value={searchTicket}
                onChange={(e) => setSearchTicket(e.target.value)}
                className="max-w-xs"
              />
            </div>
            <Tabs defaultValue="closed" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="closed">
                  Closed Orders ({reportData.closedOrdersCount})
                </TabsTrigger>
                <TabsTrigger value="open">
                  Open at Month End ({reportData.openOrdersCount})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="closed" className="mt-6">
                {renderOrdersTable(
                  reportData.closedOrders,
                  `Orders Closed in ${reportData.monthName} ${reportData.year}`,
                  reportData.closedOrdersTotal,
                  'closed'
                )}
              </TabsContent>
              <TabsContent value="open" className="mt-6">
                {renderOrdersTable(
                  reportData.openOrders,
                  `Orders Open at End of ${reportData.monthName} ${reportData.year}`,
                  reportData.openOrdersTotal,
                  'open'
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
